import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { notifyError, notifySuccess } from "./notification.slice";
import EntryTypeService from "./../services/EntryTypeService";
import { TransactionType } from "constants/SiteConstants";
import { authFailCheck } from "./auth.slice";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (Array.isArray(data) && data[0]?.errorMessage) return data[0].errorMessage;
  if (data.errors?.[0]) {
    const first = data.errors[0];
    return typeof first === "string" ? first : first.errorMessage || fallback;
  }
  return fallback;
};

const entryTypesAdapter = createEntityAdapter({
  selectId: (entryType) => entryType.id,
});

const entryTypesSlice = createSlice({
  name: "entryTypes",
  initialState: entryTypesAdapter.getInitialState({
    isLoading: false,
  }),
  reducers: {
    fetchRequest(state) {
      state.isLoading = true;
    },
    fetchFail(state, action) {
      state.isLoading = false;
    },
    fetchEntryTypesSuccess(state, action) {
      state.isLoading = false;
      entryTypesAdapter.setAll(state, action.payload);
    },
    updateEntryTypeSuccess(state, action) {
      state.isLoading = false;
      entryTypesAdapter.upsertOne(state, action.payload);
    },
    createEntryTypeSuccess(state, action) {
      state.isLoading = false;
      entryTypesAdapter.addOne(state, action.payload);
    },
    deleteEntryTypeSuccess(state, action) {
      state.isLoading = false;
      entryTypesAdapter.removeOne(state, action.payload);
    },
  },
});

export const {
  fetchRequest,
  fetchFail,
  fetchEntryTypesSuccess,
  updateEntryTypeSuccess,
  createEntryTypeSuccess,
  deleteEntryTypeSuccess,
} = entryTypesSlice.actions;

export default entryTypesSlice.reducer;

export const entryTypeSelectors = entryTypesAdapter.getSelectors(
  (state) => state.entryTypes
);

const transactionTypeSelector = (_, props) => props.transactionType;

export const makeBranchesByTransactionTypeSelector = () => {
  return createSelector(
    [selectAllEntryTypes, transactionTypeSelector],
    (entryTypes, transactionType) =>
      entryTypes.filter((e) => e.transactionType === transactionType)
  );
};

const allItem = {
  label: "Xammasi",
  value: "0",
  children: [],
};

export const incomeEntryTypesSelector = createSelector(
  [entryTypeSelectors.selectAll],
  (entryTypes) =>
    entryTypes
      .filter((x) => x.transactionType === TransactionType.Income)
      .map((item) => ({ label: item.name, value: item.name }))
);
export const expenseEntryTypesSelector = createSelector(
  [entryTypeSelectors.selectAll],
  (entryTypes) =>
    entryTypes
      .filter((x) => x.transactionType === TransactionType.Expense)
      .map((item) => ({ label: item.name, value: item.name }))
);

export const entryTypeCascadeSelector = createSelector(
  [incomeEntryTypesSelector, expenseEntryTypesSelector],
  (incomeEntryTypes, expenseEntryTypes) => {
    const defaultArray = [allItem];

    return [
      allItem,
      {
        label: "Kirim",
        value: "Income",
        children: defaultArray.concat(incomeEntryTypes),
      },
      {
        label: "Chiqim",
        value: "Expense",
        children: defaultArray.concat(expenseEntryTypes),
      },
    ];
  }
);

export const {
  selectAll: selectAllEntryTypes,
  selectById: selectEntryTypeById,
  selectIds: selectEntryTypeIds,
} = entryTypesAdapter.getSelectors((state) => state.entryTypes);

export const fetchEntryTypes = () => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await EntryTypeService.get();
  if (response.ok) {
    dispatch(fetchEntryTypesSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const updateEntryType = (entryTypeId, entryType) => async (dispatch) => {
  dispatch(fetchRequest());
  const notificationEntryType =
    entryType.transactionType === TransactionType.Income
      ? "Kirim turi"
      : "Chiqim turi";

  const response = await EntryTypeService.update(entryTypeId, entryType);

  if (response.ok) {
    dispatch(notifySuccess(`${notificationEntryType} o'zgartirildi.`));
    dispatch(fetchEntryTypes());
    return { ok: true };
  }
  dispatch(authFailCheck(response));
  dispatch(fetchFail(response.data));
  return {
    ok: false,
    message: getErrorMessage(
      response.data,
      `${notificationEntryType} o'zgartirishda xatolik ro'y berdi.`
    ),
  };
};

export const createEntryType = (entryType) => async (dispatch) => {
  dispatch(fetchRequest());
  const notificationEntryType =
    entryType.transactionType === TransactionType.Income
      ? "kirim turi"
      : "chiqim turi";

  const response = await EntryTypeService.create(entryType);

  if (response.ok) {
    dispatch(
      notifySuccess(`${entryType.name} ${notificationEntryType} yaratildi.`)
    );
    dispatch(fetchEntryTypes());
    return { ok: true };
  }
  dispatch(authFailCheck(response));
  dispatch(fetchFail(response.data));
  return {
    ok: false,
    message: getErrorMessage(
      response.data,
      `${entryType.name} ${notificationEntryType}ni yaratishda xatolik ro'y berdi.`
    ),
  };
};

export const deleteEntryType = (entryTypeId) => async (dispatch, getState) => {
  dispatch(fetchRequest());
  const currentEntryType = selectEntryTypeById(getState(), entryTypeId);
  const response = await EntryTypeService.delete(entryTypeId);
  if (response.ok) {
    dispatch(deleteEntryTypeSuccess(entryTypeId));
    dispatch(notifySuccess(`${currentEntryType.name} o'chirildi.`));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
    dispatch(
      notifyError(
        getErrorMessage(
          response.data,
          `${currentEntryType.name}ni o'chirishda xatolik ro'y berdi.`
        )
      )
    );
    dispatch(fetchFail(response.data));
  }
};
